// src/utils/shotTags.js

// Canonical keys used by the app
// (legacy: "shotType" is now "angle")
const KEY_ALIASES = {
  shotType: "angle",
};

function normKey(key) {
  const k = String(key || "").trim();
  return KEY_ALIASES[k] || k;
}

function normLang(lang) {
  const l = String(lang || "fr").toLowerCase();
  return l === "en" ? "en" : "fr";
}

const EMPTY_LABEL = {
  fr: "Aucun",
  en: "None",
};

// ------------------------------
// Tag definitions (canonical values + localized labels)
// IMPORTANT: "value" is what is stored in shots.
// ------------------------------
const TAG_DEFS = {
  shotSize: {
    fr: [
      { value: "ECU", label: "Très gros plan" },
      { value: "CU", label: "Gros plan" },
      { value: "PR", label: "Plan rapproché" },
      { value: "MCU", label: "Plan poitrine" },
      { value: "MS", label: "Plan taille" },
      { value: "PM", label: "Plan moyen" },
      { value: "CS", label: "Plan américain" },
      { value: "MFS", label: "Plan italien" },
      { value: "FS", label: "Plan large / en pied" },
    ],
    en: [
      { value: "ECU", label: "ECU — Extreme close-up" },
      { value: "CU", label: "CU — Close-up" },
      { value: "PR", label: "MCS — Medium close shot" },
      { value: "MCU", label: "MCU — Medium close-up" },
      { value: "MS", label: "MS — Medium shot" },
      { value: "PM", label: "MED — Medium (full figure)" },
      { value: "CS", label: "CS — Cowboy shot" },
      { value: "MFS", label: "MFS — Medium full shot" },
      { value: "FS", label: "FS — Full shot" },
    ],
  },

  // ✅ New name (legacy shotType)
  angle: {
    fr: [
      { value: "eye_level", label: "Plan à hauteur des yeux" },
      { value: "low_angle", label: "Contre-plongée" },
      { value: "high_angle", label: "Plongée" },
      { value: "hip_level", label: "Plan à hauteur de hanches" },
      { value: "knee_level", label: "Plan à hauteur de genoux" },
      { value: "ground_level", label: "Plan au ras du sol" },
      { value: "shoulder_level", label: "Plan à hauteur d’épaule" },
      { value: "dutch_angle", label: "Plan incliné" },
      { value: "overhead", label: "Plan zénithal" },
      { value: "aerial", label: "Plan aérien" },
    ],
    en: [
      { value: "eye_level", label: "Eye level shot" },
      { value: "low_angle", label: "Low angle shot" },
      { value: "high_angle", label: "High angle shot" },
      { value: "hip_level", label: "Hip level shot" },
      { value: "knee_level", label: "Knee level shot" },
      { value: "ground_level", label: "Ground level shot" },
      { value: "shoulder_level", label: "Shoulder level shot" },
      { value: "dutch_angle", label: "Dutch angle" },
      { value: "overhead", label: "Overhead shot" },
      { value: "aerial", label: "Aerial shot" },
    ],
  },

  focus: {
    fr: [
      { value: "rack_focus", label: "Transfert de point" },
      { value: "shallow_focus", label: "Faible profondeur de champ" },
      { value: "deep_focus", label: "Grande profondeur de champ" },
      { value: "tilt_shift", label: "Bascule et décentrement" },
      { value: "soft_focus", label: "Mise au point douce" },
      { value: "split_diopter", label: "Split diopter" },
    ],
    en: [
      { value: "rack_focus", label: "Focus pull" },
      { value: "shallow_focus", label: "Shallow focus" },
      { value: "deep_focus", label: "Deep focus" },
      { value: "tilt_shift", label: "Tilt-shift" },
      { value: "soft_focus", label: "Soft focus" },
      { value: "split_diopter", label: "Split diopter" },
    ],
  },

  framing: {
    fr: [
      { value: "single", label: "Plan solo" },
      { value: "two_shot", label: "Plan à deux" },
      { value: "three_shot", label: "Plan à trois" },
      { value: "ots", label: "Plan par-dessus l’épaule" },
      { value: "pov", label: "Plan subjectif" },
    ],
    en: [
      { value: "single", label: "Single shot" },
      { value: "two_shot", label: "Two-shot" },
      { value: "three_shot", label: "Three-shot" },
      { value: "ots", label: "Over-the-shoulder shot" },
      { value: "pov", label: "POV shot" },
    ],
  },

  movement: {
    fr: [
      { value: "static", label: "Fixe" },
      { value: "dolly", label: "Travelling" },
      { value: "zoom", label: "Zoom" },
      { value: "dolly_zoom", label: "Travelling compensé" },
      { value: "pan", label: "Panoramique" },
      { value: "tilt", label: "Tilt" },
      { value: "whip_pan", label: "Panoramique fouetté" },
      { value: "whip_tilt", label: "Tilt fouetté" },
      { value: "tracking", label: "Suivi de trépied" },
      { value: "crab", label: "Travelling latéral" },
      { value: "arc", label: "Travelling en arc" },
    ],
    en: [
      { value: "static", label: "Static / Fixed" },
      { value: "dolly", label: "Dolly" },
      { value: "zoom", label: "Zoom" },
      { value: "dolly_zoom", label: "Dolly zoom" },
      { value: "pan", label: "Pan" },
      { value: "tilt", label: "Tilt" },
      { value: "whip_pan", label: "Whip pan" },
      { value: "whip_tilt", label: "Whip tilt" },
      { value: "tracking", label: "Tracking" },
      { value: "crab", label: "Crab" },
      { value: "arc", label: "Arc" },
    ],
  },
};

// Helpers
function allItemsForKey(key) {
  const k = normKey(key);
  const def = TAG_DEFS[k];
  if (!def) return [];
  return [...(def.fr || []), ...(def.en || [])];
}

function findByValue(key, value, lang) {
  const k = normKey(key);
  const l = normLang(lang);
  const def = TAG_DEFS[k];
  if (!def) return null;
  return (def[l] || []).find((it) => it.value === value) || null;
}

function findByLabel(key, label) {
  const needle = String(label || "").trim().toLowerCase();
  if (!needle) return null;

  const items = allItemsForKey(key);
  return items.find((it) => String(it.label || "").trim().toLowerCase() === needle) || null;
}

// ------------------------------
// Public API
// ------------------------------
export function getTagSelectOptions(key, lang = "fr") {
  const k = normKey(key);
  const l = normLang(lang);

  const def = TAG_DEFS[k];
  const list = def?.[l] || [];

  // ✅ important: return a NEW array (no mutation / no shared refs)
  return [{ value: "", label: EMPTY_LABEL[l] }, ...list.map((x) => ({ ...x }))];
}

export function displayTagValue(key, value, lang = "fr") {
  const v = String(value || "").trim();
  if (!v) return "";

  const k = normKey(key);
  const l = normLang(lang);

  // If it's already canonical, show localized label
  const byVal = findByValue(k, v, l);
  if (byVal) return byVal.label;

  // Legacy: stored label instead of canonical value
  const byLab = findByLabel(k, v);
  if (byLab) {
    const localized = findByValue(k, byLab.value, l);
    return localized?.label || byLab.label || v;
  }

  // Unknown value => show as-is
  return v;
}

export function normalizeTagValue(key, value, lang = "fr") {
  const v = String(value || "").trim();
  if (!v) return "";

  const k = normKey(key);
  const l = normLang(lang);

  // Already canonical?
  if (findByValue(k, v, l) || findByValue(k, v, l === "fr" ? "en" : "fr")) {
    return v;
  }

  // Legacy label stored?
  const byLab = findByLabel(k, v);
  if (byLab) return byLab.value;

  // Special: shotSize may be stored as "ECU = Extreme Closeup" or "ECU - ..."
  if (k === "shotSize") {
    const token = v.split(/[\s=–—-]+/)[0]?.toUpperCase?.() || "";
    if (token && findByValue("shotSize", token, "fr")) return token;
  }

  return "";
}
